import React, { useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import { useQuery } from "@apollo/client/react";
import PersonOffIcon from "@mui/icons-material/PersonOff";

import useMutation from "../../hooks/useMutation";
import useNotification from "../../hooks/notification";
import useAuth from "../../hooks/useAuth";
import useRoles from "../../hooks/user/useRoles";
import CollapsibleSelect from "../../components/form/CollapsibleSelect";
import Input from "../../components/form/Input";
import ProjectAutocomplete from "../../components/form/projects/ProjectAutocomplete";
import ToggleUserActiveModal from "../../components/ToggleUserActiveModal";
import { UPDATE_USER_ACCOUNT_MUTATION } from "../../constants/graphql";
import { CLIENTS_AND_PROJECTS_QUERY } from "../../constants/graphql/queries";
import { permissions } from "../../constants/user";

const validationSchema = Yup.object().shape({
  name: Yup.string().nullable(),
  projectIds: Yup.array().nullable(),
  role: Yup.string().nullable(),
});

const ROLE_LABELS = {
  admin: "Admin",
  manager: "Warehouse Manager",
  inventory: "Warehouse Staff",
  billing_user: "Billing User",
  employee: "Inventory Lead",
  client: "Customer Admin",
  viewer: "Inventory Viewer",
  shopper: "Inventory Shopper",
};

const GROUPED_ROLES = (editorRole, currentRole) => {
  const internalStaff = [
    {
      value: "admin",
      label: "Admin",
      permission: permissions.admin,
      tooltip: "Full system access and administrative privileges.",
    },
    {
      value: "manager",
      label: "Warehouse Manager",
      permission: permissions.manager,
      tooltip:
        "Full access to inventory and ordering. Auto-assigned to new customers in their warehouse.",
    },
    {
      value: "inventory",
      label: "Warehouse Staff",
      permission: permissions.inventory,
      tooltip: "Access to inventory and fulfillment tools for daily warehouse tasks.",
    },
    {
      value: "billing_user",
      label: "Billing User",
      permission: permissions.billing_user,
      tooltip: "Read-only internal access to assigned project orders and billing reports.",
    },
  ].filter(v => v.permission >= permissions[editorRole]);

  const externalStakeholders = [
    {
      value: "client",
      label: "Customer Admin",
      permission: permissions.client,
      tooltip: "Full access to inventory and ordering for their organization.",
    },
    {
      value: "employee",
      label: "Inventory Lead",
      permission: permissions.employee,
      tooltip: "Can view inventory and place orders, but cannot edit assets.",
    },
    {
      value: "viewer",
      label: "Inventory Viewer",
      permission: permissions.viewer,
      tooltip: "Can view inventory but does not have editing permissions.",
    },
    {
      value: "shopper",
      label: "Inventory Shopper",
      permission: permissions.shopper,
      tooltip: "Can place orders via the customer storefront only.",
    },
  ].filter(v => v.permission >= permissions[editorRole]);

  const options = [
    ...(internalStaff.length > 0
      ? [{ subheader: true, label: "Internal Staff" }, ...internalStaff]
      : []),
    ...(externalStakeholders.length > 0
      ? [{ subheader: true, label: "External Stakeholders" }, ...externalStakeholders]
      : []),
  ];

  if (currentRole && !options.some(option => option.value === currentRole)) {
    options.push({
      value: currentRole,
      label: ROLE_LABELS[currentRole] || currentRole,
    });
  }

  return options;
};

export default function EditUserForm({ user, refetch, onSuccess, onClose }) {
  const { user: currentUser } = useAuth();
  const { settings } = currentUser.company;
  const isAdmin = useRoles(["admin"]);
  const { data: { clients = [] } = [] } = useQuery(CLIENTS_AND_PROJECTS_QUERY);
  const [updateUser] = useMutation(UPDATE_USER_ACCOUNT_MUTATION);
  const { notifySuccess } = useNotification();
  const [userClientsIds, setUserClientsIds] = useState(user.clientIds || []);
  const [showDisable, setShowDisable] = useState(false);

  const isCurrentUser = String(currentUser.id) === String(user.id);
  const canDisable = user.active !== false && !isCurrentUser;
  const roleOptions = useMemo(
    () => GROUPED_ROLES(currentUser.role, user.role),
    [currentUser.role, user.role]
  );

  const clientProjectsMap = useMemo(() => {
    const map = new Map();
    clients.forEach(client => {
      if (client?.projects) {
        map.set(
          client.id,
          client.projects.map(project => project.id)
        );
      }
    });
    return map;
  }, [clients]);

  const onSubmit = (values, { setErrors, setStatus, setSubmitting }) => {
    setSubmitting(true);

    const selectedClientIds = new Set();
    (values.projectIds || []).forEach(projectId => {
      for (const [clientId, projectIds] of clientProjectsMap.entries()) {
        if (projectIds.includes(projectId)) {
          selectedClientIds.add(clientId);
        }
      }
    });

    const addClientIds = [...selectedClientIds].filter(id => !userClientsIds.includes(id));
    const removeClientIds = userClientsIds.filter(id => !selectedClientIds.has(id));

    updateUser({
      variables: {
        id: user.id,
        name: values.name,
        role: values.role,
        projectIds: values.projectIds || [],
        addClientIds,
        removeClientIds,
      },
      onSuccess: ({ user: updatedUser }) => {
        setStatus({ success: true });
        setUserClientsIds(updatedUser.clientIds);
        notifySuccess("User has been updated");
        setSubmitting(false);
        refetch();
        if (onSuccess) onSuccess();
        onClose();
      },
      onFailure: errors => {
        setStatus({ success: false });
        setErrors(errors);
        setSubmitting(false);
      },
    });
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={{
          name: user.name || "",
          role: user.role || "",
          projectIds: user.projectIds || [],
        }}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
      >
        {({ errors, isSubmitting, handleSubmit, values }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Input name="name" label="User Name" size="large" />
              </Grid>
              <Grid size={12}>
                <CollapsibleSelect
                  name="role"
                  label="Permission Role"
                  options={roleOptions}
                  subheaderFontSize="1.0rem"
                  value={values.role}
                />
              </Grid>
              <Grid size={12}>
                <ProjectAutocomplete
                  allProjects={isAdmin}
                  fullWidth
                  multiple
                  group={!settings.warehouse_id_dropdown_menu}
                  name="projectIds"
                  label={`${settings.filter_field2} Permissions`}
                />
              </Grid>
            </Grid>

            {errors.submit && (
              <Box mt={2}>
                <FormHelperText error>{errors.submit}</FormHelperText>
              </Box>
            )}

            <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
              {canDisable ? (
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<PersonOffIcon />}
                  onClick={() => setShowDisable(true)}
                >
                  Disable User
                </Button>
              ) : (
                <span />
              )}
              <Button color="primary" disabled={isSubmitting} type="submit" variant="contained">
                Save
              </Button>
            </Box>
          </form>
        )}
      </Formik>

      {showDisable && (
        <ToggleUserActiveModal
          open={showDisable}
          onClose={() => setShowDisable(false)}
          entity={user}
          isDisabling
          onSuccess={() => {
            if (onSuccess) onSuccess();
            onClose();
          }}
        />
      )}
    </>
  );
}
